import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TInstallmentContext } from "../components/PaymentInstallments/installmentRules";
import { branchFeatureFlags } from "../config/branchFeatureFlags";
import { _axios } from "../utils/axios";

const BASE = "/payment-installments";
const KEY = "payment-installments";

/**
 * Whether the Admin has installments switched on. Nothing is asked of the
 * server for a municipality without the feature.
 */
export function usePaymentInstallmentSetting() {
  return useQuery({
    queryKey: [KEY, "setting"],
    queryFn: () => _axios.get<{ data: { enabled: boolean } }>(BASE),
    select: (d) => Boolean(d?.data?.data?.enabled),
    enabled: branchFeatureFlags.ENABLE_PAYMENT_INSTALLMENTS,
    staleTime: 0,
  });
}

/** Switches installments on or off. The setting is read again afterwards. */
export function useSetPaymentInstallmentSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean }) => _axios.put(BASE, body),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/**
 * Where one application stands under the installment rules. `applicationRef`
 * is the numeric id or the "AID-" number. Nothing is asked of the server for a
 * municipality without the feature, or before the application is known.
 */
export function useInstallmentContext(applicationRef?: number | string) {
  return useQuery({
    queryKey: [KEY, "application", String(applicationRef)],
    queryFn: () =>
      _axios.get<{ data: TInstallmentContext }>(
        `${BASE}/application/${encodeURIComponent(String(applicationRef))}`
      ),
    select: (d) => d?.data?.data,
    enabled: branchFeatureFlags.ENABLE_PAYMENT_INSTALLMENTS && Boolean(applicationRef),
    staleTime: 0,
  });
}

/** Removes one installment the applicant saved but has not submitted. */
export function deleteDraftReceipt(body: {
  application_id: number | string;
  receipt_id: number;
}) {
  return _axios.post(`${BASE}/delete-draft-receipt`, body);
}
